const CURRENCY_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function isNumberInvalid(value: number | null | undefined): boolean {
  return value == null || Number.isNaN(value);
}

export const abbreviateNumber = (value: number | null | undefined) => {
  if (Number.isNaN(value)) {
    return "NaN";
  }
  if (isNumberInvalid(value)) {
    return "";
  }

  const suffixes = ["", "K", "M", "B", "T", "Q"];
  if (value! < 1000) {
    return value!.toString();
  }
  const magnitude = Math.floor(Math.log10(value!) / 3);
  const scaledValue = value! / Math.pow(1000, magnitude);

  return scaledValue.toFixed(1) + suffixes[magnitude];
};

/**
 * Calculates a percentage value
 * @param count The numerator value
 * @param total The denominator value
 * @returns Calculated percentage as a number with one decimal place precision
 */
export const calculatePercentage = (count: number, total: number): number => {
  if (total <= 0) return 0;
  const percentage = (count * 100) / total;
  return Math.round(percentage * 10) / 10;
};

/**
 * Formats a number as a currency string
 * @param value The number to format
 * @param currencySymbol The currency symbol to use
 * @returns The formatted currency string
 */
export function formatCurrency(value?: number, abbreviate?: boolean): string {
  if (isNumberInvalid(value)) return "";
  if (abbreviate) {
    return "$" + abbrevNumberFormat(value);
  }
  return CURRENCY_FORMATTER.format(value!).replace(/^\$\s?/, "$");
}

/**
 * Formats a number as a number string
 * @param value The number to format
 * @param options The options to use for formatting
 * @param locale The locale to use for formatting
 * @returns The formatted number string
 */
export function formatNumber(
  value?: number,
  options?: Intl.NumberFormatOptions,
  locale: string = "en-US"
): string {
  if (isNumberInvalid(value)) return "";
  return new Intl.NumberFormat(locale, options).format(value!);
}

export function formatStandardNumber(
  value?: number,
  locale: string = "en-US"
): string {
  if (isNumberInvalid(value)) return "";
  return formatNumber(
    value,
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: true,
    },
    locale
  );
}

/**
 * Formats a number as an abbreviated number string
 * @param value The number to format
 * @param roundAbbrev Whether to round the abbreviated number
 * @returns The formatted abbreviated number string
 */
export function abbrevNumberFormat(
  value?: number,
  roundAbbrev?: boolean
): string {
  if (isNumberInvalid(value)) return "";
  const abs = Math.abs(value!);

  const formatWithSuffix = (val: number, divisor: number, suffix: string) => {
    const divided = val / divisor;
    return roundAbbrev
      ? Math.round(divided) + suffix
      : divided.toFixed(1).replace(/\.0$/, "") + suffix;
  };

  if (abs >= 1.0e12) return formatWithSuffix(value!, 1.0e12, "tn");
  if (abs >= 1.0e9) return formatWithSuffix(value!, 1.0e9, "bn");
  if (abs >= 1.0e6) return formatWithSuffix(value!, 1.0e6, "m");
  if (abs >= 1.0e3) return formatWithSuffix(value!, 1.0e3, "k");

  return value!.toString();
}

// Copy&Paste from legacy code below

type GetNumberLabelOptions = {
  /**
   * Converts the number to currency.
   */
  isCurrency?: boolean;
  /**
   * Creates a compact version of the number.
   *
   * @example
   *  getNumberLabel(10e6) // 10,000,000
   *  getNumberLabel(10e6, { compact: true }) // 10m
   */
  compact?: boolean;
};

type Range = {
  min: number;
  max: number | undefined;
};

type ParsedRange = {
  label: string;
  value: Range;
};

/**
 * Multiply a number by a million.
 *
 * @remarks
 *   useful for creating static data with big numbers using a map.
 * @example
 *   [10, 20, 30].map(toMillions);
 */
export const toMillions = (target: number) => target * 1e6;

/**
 * Shortcut for mapping an array of numbers to millions.
 */
export const millionsFrom = (target: number[]) => target.map(toMillions);

/**
 * Converts a number to a string label.
 *
 * @remarks
 *  Accepts formatting options as a second argument.
 */
export const getNumberLabel = (
  target: number | undefined,
  options: GetNumberLabelOptions = {}
) => {
  const { isCurrency, compact } = options;

  if (!target) return "";

  if (isCurrency && compact) {
    return `$${abbrevNumberFormat(target)}`;
  }

  if (compact) {
    return abbrevNumberFormat(target);
  }

  if (isCurrency) {
    return `$${formatCurrency(target)}`;
  }

  return target.toLocaleString("en-US");
};

/**
 * Gets the label for a specific range, depending on its min and max values.
 *
 * @remarks
 *  Accepts formatting options. @see {getNumberLabel}
 */
export const getRangeLabel = (
  options: Range & GetNumberLabelOptions
): string => {
  const { min, max } = options;

  const minLabel = getNumberLabel(min, options);
  const maxLabel = getNumberLabel(max, options);

  if (!min) return `Less than ${maxLabel}`;
  if (max === Number.POSITIVE_INFINITY || max === undefined)
    return `${minLabel} and above`;

  return `${minLabel} to ${maxLabel}`;
};

const toRange = (options?: GetNumberLabelOptions) => {
  return (currentLimit: number, i: number, limits: number[]): ParsedRange => {
    const previousLimit = limits[i - 1];

    return {
      label: getRangeLabel({
        min: previousLimit,
        max:
          currentLimit === Number.POSITIVE_INFINITY ? undefined : currentLimit,
        ...options,
      }),
      value: {
        min: previousLimit || 1,
        max: currentLimit - 1,
      },
    };
  };
};

/**
 * Formats an array of numbers to simple currency range objects with compact labels.
 */
export const toCompactCurrencyRange = toRange({
  isCurrency: true,
  compact: true,
});
